



















import { Component, Vue, Prop } from "vue-property-decorator";
import router from "@/router/router";
import CockpitCardComponent from "@/components/overview/CockpitCard.vue";
import {
  CockpitEntryInfo,
  CockpitEntryInfoType,
} from "./CockpitEntryInfoItem.vue";
import { formatDateHoursMinutes } from "@/utils/DateFormatter";
import { observeHyDryDeviceEventMessages } from "@/data/repository/HyDryEventsRepository";
import {
  getHyDryEventMessageDescription,
  LatestHyDryEventMessageInfo,
} from "@/data/models/HyDryEvent";
import { COLOR_COCKPIT_MANAGEMENT, COLOR_COCKPIT_MANAGEMENT_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import {
  HyDryEventMessagesFilter,
  HyDryMessageFilterType,
} from "@/utils/HyDryEventMessagesFilter";
import { MailboxFilter } from "@/utils/MailboxFilter";
import { generateRandomUuid } from "@/utils/Utils";
import { officeAccess, projectManagementAccess } from "@/permissions/UserPermissions";
import { getHyDryMessagesSettings } from "@/data/repository/SettingsRepository";

enum MessagesMode {
  OVERVIEW,
  MY_MESSAGES,
}

@Component({
  components: {
    "cockpit-card": CockpitCardComponent,
  },
})
export default class MessagesCockpitCardComponent extends Vue {
  @Prop({ default: null })
  hyDryEventMessagesFilter!: HyDryEventMessagesFilter | null;

  @Prop({ default: null })
  mailboxFilter!: MailboxFilter | null;

  get colorManagement() {
    return themed(COLOR_COCKPIT_MANAGEMENT, COLOR_COCKPIT_MANAGEMENT_DARK);
  }
  selectedMode = MessagesMode.OVERVIEW;

  allHyDryMessages: LatestHyDryEventMessageInfo[] = [];

  subscriptions: Array<() => void> = [];

  get hyDryMessagesSettings() {
    return getHyDryMessagesSettings();
  }

  async created() {
    if (this.hyDryMessagesSettings.messagesEnabled) {
      this.startObservingHyDryDeviceEvents();
    }
  }

  get isShowingMessagesOverview() {
    return this.selectedMode === MessagesMode.OVERVIEW;
  }

  // TODO: Check if this is used anywhere.
  mapMessage(unreadMessagesInfo: LatestHyDryEventMessageInfo) {
    const latestMessage = unreadMessagesInfo.unreadMessages[0].latestMessage;
    const serialNumber = unreadMessagesInfo.serialNumber;

    const id = `${unreadMessagesInfo.serialNumber}_${latestMessage.eventCategory}`;
    return {
      id: id,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.EventInfo,
      sortValue: latestMessage.messageDate.getTime(),
      data: {
        serialNumber: serialNumber,
        eventDescription: `${formatDateHoursMinutes(
          latestMessage.messageDate,
          true
        )} ${getHyDryEventMessageDescription(this, latestMessage, false)}`,
      },
    };
  }

  get messageForMailboxes(): CockpitEntryInfo[] {
    const mailboxFilter = this.mailboxFilter;

    if (!mailboxFilter) {
      return [];
    }

    return mailboxFilter
      .getMessagesInAllMailboxes(this.allHyDryMessages)
      .map((info) => {
        const numMessages = info.messages.length;

        return {
          id: info.mailboxId,
          onClickCallback: () => {
            const targetBranchId = this.hyDryEventMessagesFilter?.branchFilter
              .branch?.id;

            router.push({
              path: "cockpit/messageList",
              query: {
                ...(targetBranchId ? { targetBranchId } : {}),
                mailboxId: info.mailboxId,
                mailboxName: info.mailboxName,
                filterType: HyDryMessageFilterType.MAILBOX,
                //Add random query so that component gets recreated
                id: generateRandomUuid(),
              },
            });
          },
          shouldBeDisplayed: numMessages > 0,
          infoType: CockpitEntryInfoType.Text,
          data: {
            text: this.$t(`messages.formatNewMessagesForMailbox`, [
              numMessages,
              info.mailboxName,
            ]),
          },
        };
      });
  }

  getCockpityEntryInfoForFilterType(
    filter: HyDryEventMessagesFilter,
    filterType: HyDryMessageFilterType
  ) {
    const numMessages = filter.getFilteredMessageForFilterType(
      this.allHyDryMessages,
      filterType
    ).length;

    let stringRes = "formatNewMessagesForMe";
    const values = [this.formatNumNewMessages(numMessages)];

    switch (filterType) {
      case HyDryMessageFilterType.MY_MESSAGES:
        stringRes = "formatNewMessagesForMe";
        break;
      case HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY: {
        const targetUser = filter.targetUser;

        const hasOffice = officeAccess(targetUser);
        const hasProjectManagement = projectManagementAccess(targetUser);

        if (hasOffice && hasProjectManagement) {
          const capabilities =
            this.$t("users.role.office").toString() +
            " " +
            this.$t("common.and").toString() +
            " " +
            this.$t("users.role.projectManager").toString();

          values.push(capabilities).toString();
        } else if (hasOffice) {
          values.push(this.$t("users.role.office").toString());
        } else if (hasProjectManagement) {
          values.push(this.$t("users.role.projectManager").toString());
        }

        stringRes = "formatNewMessagesForCapability";
        break;
      }
      case HyDryMessageFilterType.ALL_MESSAGES:
        stringRes = "formatNewMessages";
        break;
    }

    return {
      id: filterType,
      onClickCallback: () => {
        const targetBranchId = this.hyDryEventMessagesFilter?.branchFilter
          .branch?.id;

        router.push({
          path: "cockpit/messageList",
          query: {
            ...(targetBranchId ? { targetBranchId } : {}),
            filterType: filterType,
            //Add random query so that component gets recreated
            id: generateRandomUuid(),
          },
        });
      },
      shouldBeDisplayed: numMessages > 0,
      infoType: CockpitEntryInfoType.Text,
      data: {
        text: this.$t(`messages.${stringRes}`, values),
      },
    };
  }

  private formatNumNewMessages(numNewMessages: number) {
    return this.$tc("messages.formatNumNewMesssages", numNewMessages);
  }

  get messageItems(): CockpitEntryInfo[] {
    const filter = this.hyDryEventMessagesFilter;
    if (!filter) {
      return [];
    }

    if (!this.hyDryMessagesSettings.messagesEnabled) {
      return [];
    }

    return [
      this.getCockpityEntryInfoForFilterType(
        filter,
        HyDryMessageFilterType.MY_MESSAGES
      ),
      ...this.messageForMailboxes,
      this.getCockpityEntryInfoForFilterType(
        filter,
        HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY
      ),
      this.getCockpityEntryInfoForFilterType(
        filter,
        HyDryMessageFilterType.ALL_MESSAGES
      ),
    ].filter((item) => item.shouldBeDisplayed);
  }

  onShowOverview() {
    return (this.selectedMode = MessagesMode.OVERVIEW);
  }

  startObservingHyDryDeviceEvents() {
    this.subscriptions.push(
      observeHyDryDeviceEventMessages((messageCollection) => {
        this.allHyDryMessages = messageCollection.latestHyDryEventMessages;
      })
    );
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => subscription());
  }

  openDeviceDetails(serialNumber: string) {
    router.push({
      path: `/main/devices/${serialNumber}`,
    });
  }
}
